<main class="cart-page">
  <nav
    class="breadcrumb"
    [attr.aria-label]="t.getRequisitionCartBreadcrumbText()"
  >
    <a routerLink="/marketplace">{{ t.getMarketplaceBrowseText() }}</a>
    <span aria-hidden="true">/</span>
    <span>{{ t.getRequisitionCartText() }}</span>
  </nav>

  <header class="page-heading">
    <h1>{{ t.getRequisitionCartText() }}</h1>
    <span class="cart-count" role="status">{{
      t.getMarketplaceSelectionCountText(cart.cartCount())
    }}</span>
  </header>

  @if (cart.cartLoading()) {
    <div class="cart-state" role="status">
      <mat-spinner diameter="32"></mat-spinner>
      {{ t.getRequisitionCartLoadingText() }}
    </div>
  } @else if (cart.cartLoadFailed()) {
    <div class="cart-state" role="alert">
      {{ t.getRequisitionCartLoadFailedText() }}
      <button mat-stroked-button type="button" (click)="cart.loadCart(true)">
        {{ t.getMarketplaceRetryText() }}
      </button>
    </div>
  } @else if (!cart.cartCount()) {
    <div class="cart-state empty-cart">
      <mat-icon aria-hidden="true">shopping_cart</mat-icon>
      <p>{{ t.getRequisitionCartEmptyText() }}</p>
      <a mat-stroked-button routerLink="/marketplace">{{
        t.getRequisitionCartBrowseText()
      }}</a>
    </div>
  } @else {
    @if (cart.cartError()) {
      <p role="alert" class="cart-error">
        {{ t.getRequisitionCartErrorText(cart.cartError()) }}
      </p>
    }

    <div class="cart-layout">
      <section
        class="selected-products"
        [attr.aria-label]="t.getRequisitionCartSelectedProductsText()"
      >
        <h2>{{ t.getRequisitionCartSelectedProductsText() }}</h2>
        <div class="cart-list">
          @for (item of cart.cartItems(); track item.productId) {
            <article class="cart-item" [class.invalid-item]="item.reason">
              @if (item.product; as product) {
                <a
                  class="item-image"
                  [routerLink]="['/marketplace', item.productId]"
                  [attr.aria-label]="
                    t.getMarketplaceViewDetailsText() + ': ' + product.name
                  "
                >
                  <app-product-image
                    [picture]="product.picture"
                    [name]="product.name"
                  />
                </a>
                <div class="item-content">
                  @if (product.brand) {
                    <span class="brand">{{ product.brand }}</span>
                  }
                  <h3>
                    <a [routerLink]="['/marketplace', item.productId]">{{
                      product.name
                    }}</a>
                  </h3>
                  <p class="seller">
                    {{ t.getMarketplaceSupplierText() }}: {{ product.seller }}
                  </p>
                  <div class="item-purchase">
                    <strong
                      class="item-price"
                      [class.unpriced]="product.priceAmount === null"
                      >{{ price(item) }}</strong
                    >
                    <div class="quantity-fact">
                      <label [for]="'quantity-' + item.productId">{{
                        t.getQuantityText()
                      }}</label>
                      <div class="quantity-control">
                        <input
                          [id]="'quantity-' + item.productId"
                          type="number"
                          [attr.inputmode]="
                            wholeUnit(item) ? 'numeric' : 'decimal'
                          "
                          [attr.min]="wholeUnit(item) ? '1' : '0'"
                          [attr.step]="wholeUnit(item) ? '1' : 'any'"
                          [value]="quantityValue(item)"
                          [disabled]="
                            cart.pendingId() !== null || !!item.reason
                          "
                          [attr.aria-invalid]="
                            invalidQuantityId === item.productId ? 'true' : null
                          "
                          [attr.aria-describedby]="
                            invalidQuantityId === item.productId
                              ? 'quantity-error-' + item.productId
                              : null
                          "
                          (input)="quantityChanged(item.productId, $event)"
                          (blur)="saveQuantity(item)"
                          (keydown.enter)="
                            saveQuantity(item); $event.preventDefault()
                          "
                        />
                        <span>{{
                          t.getMarketplaceRequestUnitValueText(
                            product.requestUnit
                          )
                        }}</span>
                      </div>
                    </div>
                  </div>
                  @if (invalidQuantityId === item.productId) {
                    <p
                      class="field-error"
                      role="alert"
                      [id]="'quantity-error-' + item.productId"
                    >
                      {{
                        wholeUnit(item)
                          ? t.getRequisitionCartWholeQuantityText()
                          : t.getRequisitionCartPositiveQuantityText()
                      }}
                    </p>
                  }
                  @if (item.reason === 'requests_disabled') {
                    <p class="item-warning" role="status">
                      <mat-icon aria-hidden="true">info_outline</mat-icon>
                      {{ t.getRequisitionCartRequestsDisabledText() }}
                    </p>
                  }
                </div>
              } @else {
                <div class="item-image unavailable-image">
                  <mat-icon aria-hidden="true">image_not_supported</mat-icon>
                </div>
                <div class="item-content unavailable-content">
                  <h3>{{ t.getRequisitionCartUnavailableText() }}</h3>
                  <p>{{ t.getQuantityText() }}: {{ item.quantity }}</p>
                </div>
              }
              <button
                mat-button
                class="remove-button"
                type="button"
                (click)="removeItem(item.productId)"
                [disabled]="cart.pendingId() !== null"
                [attr.aria-label]="
                  t.getRequisitionCartRemoveText() +
                  ': ' +
                  (item.product?.name || t.getRequisitionCartUnavailableText())
                "
              >
                <mat-icon aria-hidden="true">delete_outline</mat-icon>
                {{ t.getRequisitionCartRemoveText() }}
              </button>
            </article>
          }
        </div>
        <a routerLink="/marketplace" class="browse-link">
          <mat-icon aria-hidden="true">arrow_back</mat-icon>
          {{ t.getRequisitionCartBrowseText() }}
        </a>
      </section>

      <aside class="cart-summary">
        <h2>{{ t.getRequisitionCartNextStepText() }}</h2>
        <button
          mat-flat-button
          type="button"
          [disabled]="
            creating || cart.cartLoading() || cart.pendingId() !== null
          "
          (click)="createDraft()"
        >
          <mat-icon aria-hidden="true">note_add</mat-icon>
          {{ t.getRequisitionCartCreateText() }}
        </button>
        @if (createError) {
          <p role="alert" class="cart-error">{{ createError }}</p>
        }
      </aside>
    </div>
  }
</main>
